import { useCallback, useMemo, useState } from 'react'
import { MultiPicker } from '../components/MultiPicker'
import { useWorkspace } from '../store/AppContext'
import { useDismiss } from '../lib/hooks'
import { useRangeEntries } from '../lib/team-data'
import { EmptyState } from '../components/ui'
import { formatDur, hmShort, parseDayKey, parseDur, todayKey, weekKeys, weekStartOf } from '../lib/time'
import { entryMatches, idsOfKind, liveOfKind } from '../lib/types'
import { readStoredIds, writeStoredIds } from '../lib/util'
import type { DayKey, ID } from '../lib/types'

/** The person filter's "no filter" value. */
const EVERYONE = 'all'

export function TimesheetScreen() {
  const { ws, me, act, proj, client, confirm, canSeeAll } = useWorkspace()
  const S = ws.settings
  const [offset, setOffset] = useState(0)
  const [extra, setExtra] = useState<ID[]>([])
  // Whose week is on screen. Only roles that may see everyone's time can
  // change it; for everybody else it is permanently themselves.
  // 'all' stacks the whole team into one grid. Read-only like any view that
  // is not your own, and for a stronger reason: a number typed into a cell
  // that sums five people has no one it could belong to.
  const [viewUserId, setViewUserId] = useState<ID | typeof EVERYONE>(me.id)
  /*
   * Which clients to show. Empty means all of them.
   *
   * Remembered per workspace on this device, because the ids mean nothing in
   * another one -- and because somebody who narrows to four clients should not
   * have to do it again after visiting Reports.
   *
   * Pruned on read: a client deleted since the selection was stored would
   * otherwise filter the grid to nothing, with the row that caused it no
   * longer in the list to un-tick.
   */
  const clientItems = useMemo(
    () =>
      liveOfKind(ws.clients, 'client')
        .filter((c) => c.status === 'active')
        .map((c) => ({ id: c.id, name: c.name })),
    [ws.clients],
  )

  const clientsKey = 'cadence.timesheet.clients.' + ws.id
  const [clientIds, setClientIds] = useState<ID[]>(() =>
    // Pruned to clients, not merely to rows that exist: a stored id that is
    // now a prospect would narrow the grid to projects it no longer lists,
    // leaving an empty week with no chip on screen to explain it.
    readStoredIds(clientsKey, (id) => liveOfKind(ws.clients, 'client').some((c) => c.id === id)),
  )

  const chooseClients = useCallback(
    (ids: ID[]) => {
      setClientIds(ids)
      writeStoredIds(clientsKey, ids)
    },
    [clientsKey],
  )
  const [search, setSearch] = useState('')
  // Collapsed client groups, by client id. A Set rather than a flag per
  // group so it survives the list changing underneath it.
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set())
  const [pickerOpen, setPickerOpen] = useState(false)
  const closePicker = useCallback(() => setPickerOpen(false), [])
  const pickerRef = useDismiss<HTMLDivElement>(pickerOpen, closePicker)

  const wk = useMemo(() => weekKeys(offset, S.weekStart), [offset, S.weekStart])

  /**
   * Jump to whichever week a date falls in.
   *
   * The grid is addressed by week offset, so the date is converted rather
   * than stored: find the start of its week, and of this one, and count the
   * weeks between. Going through week starts rather than dividing the raw day
   * difference is what makes it land correctly from any day of the week.
   */
  const jumpToDate = (value: string) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return
    const target = weekStartOf(0, S.weekStart, parseDayKey(value))
    const current = weekStartOf(0, S.weekStart, new Date())
    setOffset(Math.round((target.getTime() - current.getTime()) / (7 * 86400000)))
  }

  /** Project rows opened to show who did the work. Everyone view only. */
  const [openRows, setOpenRows] = useState<Set<ID>>(() => new Set())

  const toggleRow = (pid: ID) =>
    setOpenRows((prev) => {
      const next = new Set(prev)
      if (next.has(pid)) next.delete(pid)
      else next.add(pid)
      return next
    })

  const toggleGroup = (id: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const viewingEveryone = viewUserId === EVERYONE
  const viewing = ws.team.find((m) => m.id === viewUserId)
  const viewingOther = viewUserId !== me.id

  /** Whether an entry belongs in the grid as currently filtered. */
  const inView = useCallback(
    (e: { userId: ID }) => viewingEveryone || e.userId === viewUserId,
    [viewingEveryone, viewUserId],
  )
  const today = todayKey()

  // Editing rewrites a whole day's entries for a project, so somebody else's
  // week is read-only here. Correcting another person's time is still
  // possible entry by entry on the Timer screen, where the change is visible
  // and one row at a time.
  const readOnly = viewingOther

  // Your own week comes from the snapshot; somebody else's is fetched for
  // exactly that week and nothing more.
  const { entries: otherEntries } = useRangeEntries(
    ws.id,
    { from: wk[0], to: wk[wk.length - 1], userId: viewingEveryone ? undefined : viewUserId },
    viewingOther,
  )
  const sourceEntries = viewingOther ? otherEntries : ws.entries

  /*
   * Prospect work is not in this grid.
   *
   * The timesheet groups by client, narrows by client and rolls a week up per
   * client -- it is a client view of the week, and a prospect row in it would
   * put the two populations in one column of figures. Prospect time is logged
   * and read on the Prospects screen, which is the only place it appears.
   */
  const prospectIds = useMemo(() => idsOfKind(ws.clients, 'prospect'), [ws.clients])
  const ownProject = useCallback(
    (projectId: ID | null | undefined) => {
      const clientId = projectId ? ws.projects.find((p) => p.id === projectId)?.clientId : null
      return !(clientId && prospectIds.has(clientId))
    },
    [ws.projects, prospectIds],
  )

  /* ---- rows ---- */
  const rowIds = useMemo(() => {
    const ids: ID[] = []

    // Projects with time on them this week come first, so the week's actual
    // work is at the top rather than wherever it happens to fall
    // alphabetically among everything else.
    for (const e of sourceEntries) {
      if (
        inView(e) &&
        wk.includes(e.day) &&
        e.projectId &&
        ownProject(e.projectId) &&
        !ids.includes(e.projectId)
      ) {
        ids.push(e.projectId)
      }
    }

    // Then every other active project, so the grid is ready to type into
    // without hunting for a row first. Archived ones stay out: they are
    // retired, and a row for each would grow forever.
    for (const p of ws.projects) {
      if (p.status === 'active' && ownProject(p.id) && !ids.includes(p.id)) ids.push(p.id)
    }

    // Rows added by hand -- an archived project somebody still needs to log
    // against -- are kept even though the loop above skips them.
    for (const id of extra) if (ownProject(id) && !ids.includes(id)) ids.push(id)

    // Narrowing by client hides rows, never their hours: the week total keeps
    // counting what is filtered out, so the filter cannot make a timesheet
    // look emptier than it is.
    const wanted = new Set(clientIds)
    const narrowed = wanted.size
      ? ids.filter((id) => {
          const c = proj(id)?.clientId
          return c ? wanted.has(c) : false
        })
      : ids

    // Same rule as the Timer screen's filter, so "C100" means the same thing
    // in both places. A client is reached through the project, which is the
    // only way a row knows one.
    const q = search.trim()
    if (!q) return narrowed
    return narrowed.filter((id) => {
      const p = proj(id)
      if (!p) return false
      return entryMatches(q, { projectName: p.name, clientName: client(p.clientId)?.name })
    })
  }, [sourceEntries, ws.projects, inView, wk, extra, clientIds, proj, client, search, ownProject])

  /**
   * Rows grouped by the client that owns them, matching the Projects screen
   * and the picker. Three places listing the same thing three different ways
   * was the actual problem.
   *
   * Ordering inside a group is inherited from rowIds, so projects with time
   * on them this week still come first within their client.
   */
  const groups = useMemo(() => {
    const buckets = new Map<string, { name: string; ids: ID[] }>()

    for (const pid of rowIds) {
      const c = client(proj(pid)?.clientId)
      const key = c?.id ?? '~none'
      const bucket = buckets.get(key) ?? { name: c?.name ?? 'No client', ids: [] }
      bucket.ids.push(pid)
      buckets.set(key, bucket)
    }

    return [...buckets.entries()]
      .map(([id, v]) => ({ id, ...v }))
      .sort((a, b) => {
        if (a.id === '~none') return 1
        if (b.id === '~none') return -1
        return a.name.localeCompare(b.name)
      })
  }, [rowIds, proj, client])

  const cellSec = useCallback(
    (pid: ID, day: DayKey) =>
      sourceEntries
        .filter((e) => inView(e) && e.projectId === pid && e.day === day)
        .reduce((a, e) => a + e.dur, 0),
    [sourceEntries, inView],
  )

  /**
   * Who logged time on a project this week, biggest contributor first.
   *
   * Only meaningful in the Everyone view -- with one person selected the
   * answer is always that person, and a caret that opens to a single row
   * repeating the heading is noise.
   */
  const peopleOn = useCallback(
    (pid: ID) => {
      const byUser = new Map<ID, number>()
      for (const e of sourceEntries) {
        if (e.projectId !== pid || !wk.includes(e.day) || !inView(e)) continue
        byUser.set(e.userId, (byUser.get(e.userId) ?? 0) + e.dur)
      }
      return [...byUser.entries()]
        .map(([userId, sec]) => ({
          userId,
          sec,
          // Somebody who has since left still did the work, so fall back to a
          // label rather than dropping the row or calling them nobody.
          name: ws.team.find((m) => m.id === userId)?.name ?? 'Former member',
        }))
        .sort((a, b) => b.sec - a.sec || a.name.localeCompare(b.name))
    },
    [sourceEntries, wk, inView, ws.team],
  )

  const cellSecFor = useCallback(
    (pid: ID, day: DayKey, userId: ID) =>
      sourceEntries
        .filter((e) => e.userId === userId && e.projectId === pid && e.day === day)
        .reduce((a, e) => a + e.dur, 0),
    [sourceEntries],
  )

  const setCell = useCallback(
    (pid: ID, day: DayKey, raw: string) => {
      void act.setCell(pid, day, parseDur(raw))
    },
    [act],
  )

  const colTotals = wk.map((k) => rowIds.reduce((a, pid) => a + cellSec(pid, k), 0))
  const weekTotal = colTotals.reduce((a, b) => a + b, 0)

  const ws0 = parseDayKey(wk[0])
  const ws6 = parseDayKey(wk[6])
  const weekLabel =
    ws0.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) +
    ' – ' +
    ws6.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })

  const copyLastWeek = () => {
    void act.copyWeek(weekKeys(offset - 1, S.weekStart), wk)
  }


  const addable = ws.projects.filter((p) => ownProject(p.id) && !rowIds.includes(p.id))

  return (
    <section className="screen">
      {/* ---------- controls ---------- */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10 }}>
        <div className="weeknav">
          <button type="button" onClick={() => setOffset((o) => o - 1)} aria-label="Previous week">
            ‹
          </button>
          <span>{weekLabel}</span>
          <button type="button" onClick={() => setOffset((o) => o + 1)} aria-label="Next week">
            ›
          </button>
        </div>
        <button type="button" className="btn" onClick={() => setOffset(0)} disabled={offset === 0}>
          This week
        </button>
        <button type="button" className="btn" onClick={copyLastWeek} disabled={readOnly}>
          Copy last week
        </button>
        <input
          type="date"
          className="input"
          style={{ width: 160, padding: '8px 10px' }}
          aria-label="Jump to the week containing this date"
          value={wk[0]}
          onChange={(e) => jumpToDate(e.target.value)}
        />

        {canSeeAll && (
          <select
            className="role-select"
            aria-label="Whose timesheet to show"
            value={viewUserId}
            onChange={(e) => setViewUserId(e.target.value)}
          >
            <option value={EVERYONE}>Everyone</option>
            {ws.team
              .filter((m) => m.status === 'active')
              .map((m) => (
                <option key={m.id} value={m.id}>
                  {m.id === me.id ? `${m.name} (you)` : m.name}
                </option>
              ))}
          </select>
        )}

        <input
          className="input"
          style={{ flex: '1 1 180px', maxWidth: 260, padding: '8px 11px' }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search project or client…"
          aria-label="Search by project or client"
        />

        <MultiPicker
          items={clientItems}
          selected={clientIds}
          onChange={chooseClients}
          allLabel="All clients"
          unit={['client', 'clients']}
          searchPlaceholder="Search clients…"
          ariaLabel="Filter by client"
          emptyText="No clients yet."
        />

        <div className="push row" style={{ gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12.5, color: 'var(--mut)' }}>Week total</span>
          <span className="mono" style={{ fontSize: 17, fontWeight: 600 }}>
            {formatDur(weekTotal, S.durFormat)}
          </span>
        </div>
      </div>

      {readOnly && (
        <div
          className="row"
          style={{
            gap: 8,
            padding: '10px 14px',
            borderRadius: 10,
            background: 'var(--ac-soft)',
            border: '1px solid var(--ac-line)',
            color: '#8C3A1C',
            fontSize: 13,
          }}
        >
          <span aria-hidden>👁</span>
          {viewingEveryone ? (
            <>
              Showing <b style={{ margin: '0 2px' }}>the whole team</b> — every project row adds up
              everyone’s hours, so the grid is read-only. Pick a person to edit a week.
            </>
          ) : (
            <>
              Viewing <b style={{ margin: '0 2px' }}>{viewing?.name ?? 'someone else'}</b>’s week —
              read-only. Correct their time entry by entry on the Timer screen.
            </>
          )}
        </div>
      )}


      {/* ---------- grid ---------- */}
      {rowIds.length === 0 ? (
        <EmptyState
          title={clientIds.length ? 'No projects for those clients' : 'No projects yet'}
          body={
            clientIds.length
              ? 'None of the selected clients has an active project. Choose others, or clear the filter to see everything.'
              : 'Every active project gets a row here automatically. Create one and it will appear.'
          }
          action={addable.length ? { label: 'Add a project row', onClick: () => setPickerOpen(true) } : undefined}
        />
      ) : null}

      <div className="card" style={{ overflowX: 'auto' }}>
        <div style={{ minWidth: 820 }}>
          <div className="sheet-grid sheet-head">
            <div style={{ padding: '11px 16px' }} className="eyebrow">
              Project
            </div>
            {wk.map((k) => {
              const d = parseDayKey(k)
              return (
                <div
                  key={k}
                  style={{
                    padding: '9px 12px',
                    textAlign: 'center',
                    background: k === today ? 'var(--ac-soft)' : 'transparent',
                  }}
                >
                  <div className="eyebrow" style={{ letterSpacing: '.06em' }}>
                    {d.toLocaleDateString(undefined, { weekday: 'short' })}
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 600, marginTop: 2 }}>{d.getDate()}</div>
                </div>
              )
            })}
            <div className="eyebrow" style={{ padding: '11px 12px', textAlign: 'right' }}>
              Total
            </div>
          </div>

          {groups.map((group) => {
            // While searching, every group is open: a row that matched but
            // stayed hidden in a collapsed client would read as no result.
            const isCollapsed = !search.trim() && collapsed.has(group.id)
            const groupTotal = group.ids.reduce(
              (a, pid) => a + wk.reduce((b, k) => b + cellSec(pid, k), 0),
              0,
            )

            return (
            <div key={group.id}>
              {/* Client heading. It spans the whole grid rather than sitting
                  in the project column, so it reads as a divider between
                  groups and not as a row with six empty days.

                  Collapsing shows the group's week total in its place, so
                  folding a client away hides the detail without hiding the
                  fact that there are hours in there. */}
              <button
                type="button"
                aria-expanded={!isCollapsed}
                onClick={() => toggleGroup(group.id)}
                style={{
                  width: '100%',
                  padding: '9px 16px',
                  background: 'var(--canvas)',
                  borderTop: '1px solid var(--line)',
                  borderBottom: '1px solid var(--line)',
                  borderLeft: 0,
                  borderRight: 0,
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: 8,
                  cursor: 'pointer',
                  font: 'inherit',
                  textAlign: 'left',
                }}
              >
                <span
                  aria-hidden
                  style={{
                    fontSize: 10,
                    color: 'var(--mut)',
                    // Rotating one glyph keeps the two states visually
                    // related, rather than swapping to a different shape.
                    transform: isCollapsed ? 'rotate(-90deg)' : 'none',
                    display: 'inline-block',
                    width: 10,
                  }}
                >
                  ▾
                </span>
                <span
                  className="eyebrow"
                  style={{ letterSpacing: '.06em', flex: 1, minWidth: 0 }}
                >
                  {group.name}
                </span>
                {isCollapsed && groupTotal > 0 && (
                  <span className="mono" style={{ fontSize: 12, fontWeight: 600 }}>
                    {formatDur(groupTotal, S.durFormat)}
                  </span>
                )}
                <span style={{ fontSize: 11.5, color: 'var(--faint)' }}>
                  {group.ids.length} {group.ids.length === 1 ? 'project' : 'projects'}
                </span>
              </button>

              {!isCollapsed && group.ids.map((pid) => {
                const p = proj(pid)
                const rowTotal = wk.reduce((a, k) => a + cellSec(pid, k), 0)
                const who = viewingEveryone ? peopleOn(pid) : []
                const isOpen = openRows.has(pid)
                return (
                  <div key={pid}>
                  <div key={pid} className="sheet-grid sheet-row">
                    <div style={{ padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 9, minWidth: 0 }}>
                      {viewingEveryone && who.length > 0 && (
                        <button
                          type="button"
                          onClick={() => toggleRow(pid)}
                          aria-expanded={isOpen}
                          aria-label={'Who worked on ' + (p?.name ?? 'this project')}
                          title="Who worked on this"
                          style={{
                            border: 0,
                            background: 'transparent',
                            cursor: 'pointer',
                            padding: 0,
                            width: 14,
                            color: 'var(--mut)',
                            fontSize: 10,
                            transform: isOpen ? 'none' : 'rotate(-90deg)',
                            transition: 'transform .12s',
                          }}
                        >
                          ▾
                        </button>
                      )}
                      <span className="dot" style={{ background: p?.color ?? '#CFCABC' }} aria-hidden />
                      <div className="minw0">
                        <div className="trunc" style={{ fontSize: 13.5, fontWeight: 500 }}>
                          {p?.name ?? 'Deleted project'}
                        </div>
                      </div>
                      {viewingEveryone && who.length > 0 && !isOpen && (
                        <span style={{ fontSize: 11.5, color: 'var(--faint)', whiteSpace: 'nowrap' }}>
                          {who.length === 1 ? who[0].name : who.length + ' people'}
                        </span>
                      )}
                      <button
                        type="button"
                        className="ibtn push"
                        style={{ width: 22, height: 22, fontSize: 12 }}
                        title="Clear row"
                        aria-label={`Clear ${p?.name ?? 'row'} for this week`}
                        disabled={readOnly}
                        onClick={() =>
                          confirm({
                            title: 'Clear this row?',
                            body: `Deletes your time on ${p?.name ?? 'this project'} for ${weekLabel}. Other people's entries are untouched.`,
                            cta: 'Clear row',
                            danger: true,
                            onConfirm: () => {
                              void act.clearRow(pid, wk)
                              setExtra((x) => x.filter((id) => id !== pid))
                            },
                          })
                        }
                      >
                        ✕
                      </button>
                    </div>
                    {wk.map((k) => (
                      <Cell
                        key={k}
                        sec={cellSec(pid, k)}
                        today={k === today}
                        label={`${p?.name ?? 'Project'} on ${k}`}
                        onCommit={(v) => setCell(pid, k, v)}
                        readOnly={readOnly}
                      />
                    ))}
                    <div
                      className="mono"
                      style={{ padding: '10px 12px', textAlign: 'right', fontSize: 14, fontWeight: 500 }}
                    >
                      {rowTotal ? formatDur(rowTotal, S.durFormat) : '—'}
                    </div>
                  </div>

                  {/* Who did it. Indented under the project and in the same
                      grid, so the days still line up with the header. */}
                  {isOpen &&
                    who.map((person) => (
                      <div
                        key={person.userId}
                        className="sheet-grid"
                        style={{ background: 'var(--canvas)', borderBottom: '1px solid var(--line2)' }}
                      >
                        <div
                          className="trunc"
                          style={{ padding: '7px 16px 7px 48px', fontSize: 12.5, color: 'var(--mut)' }}
                        >
                          {person.name}
                        </div>
                        {wk.map((k) => {
                          const sec = cellSecFor(pid, k, person.userId)
                          return (
                            <div
                              key={k}
                              className="mono"
                              style={{
                                padding: '7px 4px',
                                textAlign: 'center',
                                fontSize: 12.5,
                                color: sec ? 'var(--mut)' : 'var(--faint)',
                              }}
                            >
                              {sec ? hmShort(sec) : '·'}
                            </div>
                          )
                        })}
                        <div
                          className="mono"
                          style={{ padding: '7px 12px', textAlign: 'right', fontSize: 12.5, color: 'var(--mut)' }}
                        >
                          {formatDur(person.sec, S.durFormat)}
                        </div>
                      </div>
                    ))}
                  </div>
                )
              })}
            </div>
            )
          })}

          <div className="sheet-grid sheet-foot">
            <div style={{ padding: '12px 16px' }}>
              {/* Every active project already has a row, so this is only for
                  the archived ones. Hidden rather than greyed out when there
                  is nothing left to add: a permanently dead control is just
                  noise. */}
              <div
                ref={pickerRef}
                style={{
                  position: 'relative',
                  display: !readOnly && addable.length ? 'inline-block' : 'none',
                }}
              >
                <button
                  type="button"
                  className="linkbtn linkbtn--ac"
                  aria-expanded={pickerOpen}
                  aria-haspopup="menu"
                  onClick={() => setPickerOpen((v) => !v)}
                >
                  + Add archived project
                </button>
                {pickerOpen && (
                  <div className="popover popover--up" style={{ left: 0, width: 260, padding: 6 }} role="menu">
                    {addable.map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        role="menuitem"
                        className="popover-row"
                        onClick={() => {
                          setExtra((x) => [...x, p.id])
                          setPickerOpen(false)
                        }}
                      >
                        <span className="dot" style={{ background: p.color }} aria-hidden />
                        <span className="trunc">{p.name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            {colTotals.map((sec, i) => (
              <div
                key={wk[i]}
                className="mono"
                style={{
                  padding: '12px 8px',
                  textAlign: 'center',
                  fontSize: 13,
                  fontWeight: 500,
                  color: sec ? 'var(--ink)' : 'var(--faint)',
                  background: wk[i] === today ? 'rgba(220,91,52,.045)' : 'transparent',
                }}
              >
                {sec ? formatDur(sec, S.durFormat) : '—'}
              </div>
            ))}
            <div className="mono" style={{ padding: 12, textAlign: 'right', fontSize: 15, fontWeight: 600 }}>
              {formatDur(weekTotal, S.durFormat)}
            </div>
          </div>
        </div>
      </div>

      <div style={{ fontSize: 12.5, color: 'var(--mut)' }} className="pretty">
        Type <code className="kbd">1:30</code> or <code className="kbd">1.5</code> — both mean an hour and a half. Edits
        here rewrite that day's entries for the project.
      </div>
    </section>
  )
}

/* ------------------------------------------------------------------ */

function Cell({
  sec,
  today,
  label,
  onCommit,
  readOnly = false,
}: {
  sec: number
  today: boolean
  label: string
  onCommit: (v: string) => void
  readOnly?: boolean
}) {
  const [editing, setEditing] = useState<string | null>(null)
  const value = editing ?? hmShort(sec)

  const commit = () => {
    if (readOnly) {
      setEditing(null)
      return
    }
    if (editing !== null && editing !== hmShort(sec)) onCommit(editing)
    setEditing(null)
  }

  return (
    <div className={today ? 'sheet-cell sheet-cell--today' : 'sheet-cell'}>
      <input
        value={value}
        placeholder="0:00"
        aria-label={label}
        readOnly={readOnly}
        tabIndex={readOnly ? -1 : undefined}
        onChange={(e) => setEditing(e.target.value)}
        onFocus={() => setEditing(hmShort(sec))}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.currentTarget.blur()
          } else if (e.key === 'Escape') {
            setEditing(null)
            e.currentTarget.blur()
          }
        }}
      />
    </div>
  )
}
